import { isServiceWorkerEnabled, SERVICE_WORKER_PATH } from "./serviceWorker/policy";

/*
 * Page side of the Lyft Zone service worker (Workout Log V1 blueprint §9.7,
 * §22.3). Deliberately minimal:
 * - it registers `/service-worker.js` only in a production build whose
 *   REACT_APP_SERVICE_WORKER_ENABLED is exactly 'true';
 * - it never reloads or navigates the page, never shows update UI, never calls
 *   skipWaiting() on a waiting worker and never listens for controllerchange:
 *   a new version waits until every tab of the old one has closed;
 * - it never reads or writes IndexedDB or localStorage.
 *
 * Kill mode (flag not exactly 'true') is authoritative inside the worker file
 * itself. The page registers nothing; as defense in depth it only asks an
 * already installed Lyft Zone registration to check for an update, which in a
 * kill-mode deployment fetches the kill worker so the worker retires itself
 * (owned caches first, then the registration). The page never unregisters and
 * never deletes caches on its own.
 */

function whenLoaded(windowObject, callback) {
  if (windowObject.document?.readyState === "complete") {
    callback();
  } else {
    windowObject.addEventListener("load", callback, { once: true });
  }
}

function isLyftZoneRegistration(registration, scriptUrl) {
  const worker = registration?.active || registration?.waiting || registration?.installing;
  return worker?.scriptURL === scriptUrl;
}

async function requestKillWorkerUpdate(container, scriptUrl) {
  try {
    const registrations = await container.getRegistrations();
    await Promise.all(
      registrations
        .filter((registration) => isLyftZoneRegistration(registration, scriptUrl))
        .map((registration) => registration.update().catch(() => undefined))
    );
  } catch (_error) {
    // Best effort only: the browser's own update checks still reach the worker.
  }
}

/**
 * Returns the mode it applied: "skipped" (development, test or no service
 * worker support), "disabled" (kill mode) or "enabled".
 */
export function registerServiceWorker({
  // Individual keys, so the build inlines three literals, not the whole env.
  env = {
    NODE_ENV: process.env.NODE_ENV,
    PUBLIC_URL: process.env.PUBLIC_URL,
    REACT_APP_SERVICE_WORKER_ENABLED: process.env.REACT_APP_SERVICE_WORKER_ENABLED,
  },
  windowObject = typeof window === "undefined" ? null : window,
} = {}) {
  const container = windowObject?.navigator?.serviceWorker;
  if (env.NODE_ENV !== "production" || !container) return "skipped";

  const scriptPath = `${env.PUBLIC_URL || ""}${SERVICE_WORKER_PATH}`;
  if (!isServiceWorkerEnabled(env.REACT_APP_SERVICE_WORKER_ENABLED)) {
    const scriptUrl = new URL(scriptPath, windowObject.location.href).href;
    whenLoaded(windowObject, () => {
      void requestKillWorkerUpdate(container, scriptUrl);
    });
    return "disabled";
  }

  whenLoaded(windowObject, () => {
    container.register(scriptPath).catch((error) => {
      console.warn("Lyft Zone service worker registration failed.", error);
    });
  });
  return "enabled";
}
